// Panel przypisanych pokoi z możliwością edycji i zarządzania

import React from "react";
import { useAccommodation } from "../../AccommodationProvider";
// import { AccommodationHelpers } from "../../utils/accommodationHelpers";
import AssignedRoomCard from "./AssignedRoomCard";
import { MiniDaySwitcher } from "./MiniDaySwitcher";

const AssignedRoomsPanel: React.FC = () => {
  const {
    assignedRooms,
    displayOptions,
    toggleGroupCollapse,
    currentDayIndex,
    setCurrentDay,
    accommodationDays,
  } = useAccommodation();

  // Grupuj pokoje według typu przypisania
  const groupedRooms = React.useMemo(() => {
    const groups = {
      pairs: assignedRooms.filter((room) => room.occupancyType === "pairs"),
      singles_shared: assignedRooms.filter(
        (room) => room.occupancyType === "singles_shared"
      ),
      singles_alone: assignedRooms.filter(
        (room) => room.occupancyType === "singles_alone"
      ),
    };

    return groups;
  }, [assignedRooms]);

  // Oblicz statystyki dla każdej grupy
  const groupStats = React.useMemo(() => {
    return {
      pairs: {
        rooms: groupedRooms.pairs.length,
        persons: groupedRooms.pairs.reduce(
          (sum, room) => sum + room.quantity * 2,
          0
        ),
        totalCost: groupedRooms.pairs.reduce(
          (sum, room) => sum + room.quantity * (room.unitPrice || 0),
          0
        ),
      },
      singles_shared: {
        rooms: groupedRooms.singles_shared.length,
        persons: groupedRooms.singles_shared.reduce(
          (sum, room) =>
            sum +
            room.quantity *
              (room.personsCountPerRoom ||
                room.categoryOption.personsPerRoom ||
                1),
          0
        ),
        totalCost: groupedRooms.singles_shared.reduce(
          (sum, room) => sum + room.quantity * (room.unitPrice || 0),
          0
        ),
      },
      singles_alone: {
        rooms: groupedRooms.singles_alone.length,
        persons: groupedRooms.singles_alone.reduce(
          (sum, room) => sum + room.quantity * 1,
          0
        ),
        totalCost: groupedRooms.singles_alone.reduce(
          (sum, room) => sum + room.quantity * (room.unitPrice || 0),
          0
        ),
      },
    };
  }, [groupedRooms]);

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with Mini Day Navigation */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            📋 Przypisane pokoje
          </h3>

          {/* Mini Day Navigation - reusable component */}
          <MiniDaySwitcher
            days={accommodationDays}
            currentDayIndex={currentDayIndex}
            onDayChange={setCurrentDay}
          />
        </div>
      </div>

      {/* Lista pokoi pogrupowana */}
      <div className="p-4">
        {assignedRooms.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">🏨</div>
            <p>Brak przypisanych pokoi</p>
            <p className="text-sm mt-1">
              Dodaj pokoje z panelu dostępnych pokoi
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Grupa: Pary */}
            {groupedRooms.pairs.length > 0 && (
              <div>
                <button
                  onClick={() => toggleGroupCollapse("pairs")}
                  className="w-full flex items-center justify-between p-3 bg-pink-50 border border-pink-200 rounded-lg hover:bg-pink-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">👫</span>
                    <span className="font-medium text-pink-800">
                      Pary: {groupStats.pairs.persons} os.
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-pink-600">
                      {displayOptions.collapsedGroups.has("pairs") ? "▶" : "▼"}
                    </span>
                  </div>
                </button>

                {!displayOptions.collapsedGroups.has("pairs") && (
                  <div className="mt-2 space-y-2">
                    {groupedRooms.pairs.map((room) => (
                      <AssignedRoomCard key={room.id} room={room} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Grupa: Single (dzielą) */}
            {groupedRooms.singles_shared.length > 0 && (
              <div>
                <button
                  onClick={() => toggleGroupCollapse("singles_shared")}
                  className="w-full flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">👤</span>
                    <span className="font-medium text-blue-800">
                      Single: {groupStats.singles_shared.persons} os.
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-blue-600">
                      {displayOptions.collapsedGroups.has("singles_shared")
                        ? "▶"
                        : "▼"}
                    </span>
                  </div>
                </button>

                {!displayOptions.collapsedGroups.has("singles_shared") && (
                  <div className="mt-2 space-y-2">
                    {groupedRooms.singles_shared.map((room) => (
                      <AssignedRoomCard key={room.id} room={room} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Grupa: Single (prywatnie) */}
            {groupedRooms.singles_alone.length > 0 && (
              <div>
                <button
                  onClick={() => toggleGroupCollapse("singles_alone")}
                  className="w-full flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">👤</span>
                    <span className="font-medium text-purple-800">
                      Single pryw.: {groupStats.singles_alone.persons} os.
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-purple-600">
                      {displayOptions.collapsedGroups.has("singles_alone")
                        ? "▶"
                        : "▼"}
                    </span>
                  </div>
                </button>

                {!displayOptions.collapsedGroups.has("singles_alone") && (
                  <div className="mt-2 space-y-2">
                    {groupedRooms.singles_alone.map((room) => (
                      <AssignedRoomCard key={room.id} room={room} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignedRoomsPanel;
